import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { createApiClient } from '../lib/api-client.js';
import type { CurrentTenant, CurrentUser } from '../lib/types.js';

interface AuthState {
  status: 'loading' | 'anonymous' | 'authenticated';
  user: CurrentUser | null;
  /** 工厂端身份才有；内部飞书账号是 null */
  tenant: CurrentTenant | null;
  refresh: () => Promise<void>;
}

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{
    status: 'loading' | 'anonymous' | 'authenticated';
    user: CurrentUser | null;
    tenant: CurrentTenant | null;
  }>({ status: 'loading', user: null, tenant: null });

  async function fetchMe() {
    try {
      const api = createApiClient();
      const res = await api.me();
      if (res.user) {
        setState({
          status: 'authenticated',
          user: res.user,
          tenant: (res as { tenant?: CurrentTenant | null }).tenant ?? null,
        });
      } else {
        setState({ status: 'anonymous', user: null, tenant: null });
      }
    } catch {
      setState({ status: 'anonymous', user: null, tenant: null });
    }
  }

  useEffect(() => {
    void fetchMe();
  }, []);

  return <AuthCtx.Provider value={{ ...state, refresh: fetchMe }}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(AuthCtx);
  if (!v) throw new Error('useAuth must be inside <AuthProvider>');
  return v;
}
